import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import moment, { Moment } from 'moment'
import { isEmpty } from 'underscore'
import { ADVANCED_ANALYTICS, IPlotVariable, IBestDataset } from '../../../../types/analytics.d'
import { IAdvancedAnalyticsApiPayload } from '../../../../types/chart'
import { IEnrichedMetadata } from '../../../../types/metadata'
import { DT0Format } from '../../../../utils/StaticVariable'
import { IAnalytcisUserInput } from '../../../../types/IAnalyticsUserInput'
import { MarkerItem } from '../../../../types/home'

interface UseExtremeValuesParams {
  coordinates: number[]
  previewDatasetId: string
  datasets: IBestDataset[]
  onDatasetChange: (item: string) => void
  dateRange: [moment.Moment, moment.Moment]
  enrichedMetadata: IEnrichedMetadata[]
  onUpdate: (payload: IAdvancedAnalyticsApiPayload) => void
  extremeValuesImage: string
  extremeValuesLinks: string[]
  error: string
  updating: boolean
  pointName: string
  userInput: IAnalytcisUserInput
  updateUserInput: React.Dispatch<Partial<IAnalytcisUserInput>>
  point?: MarkerItem
  globalDateRange: any[]
  setGlobalDateRange: (item: any) => void
  isForAllAnalytics: boolean
  setIsForAllAnalytics: (item: any) => void
}

export const useExtremeValuesParams = (params: UseExtremeValuesParams) => {
  const {
    coordinates,
    previewDatasetId,
    datasets,
    dateRange,
    enrichedMetadata,
    onUpdate,
    extremeValuesImage,
    pointName,
    userInput,
    updateUserInput,
    onDatasetChange,
    globalDateRange,
    setGlobalDateRange,
    isForAllAnalytics,
    setIsForAllAnalytics
  } = params

  const selfTriggered = useRef(false)

  const [variablesX, setVariablesX] = useState<IPlotVariable[]>([])
  const [dataset, setDataset] = useState(userInput.ExtremeValues.dataset)
  const [variableX, setVariableX] = useState<IPlotVariable>()
  const [variableXIndex, setVariableXIndex] = useState(userInput.ExtremeValues.variable)
  const [startDate, setStartDate] = useState(userInput.ExtremeValues.startDate || moment(dateRange[0]))
  const [endDate, setEndDate] = useState(userInput.ExtremeValues.endDate || moment(dateRange[1]))

  const getImage = useCallback(
    (
      indexX: IPlotVariable,
      orderId: string,
      startDate: string,
      endDate: string
    ) => {
      const [longitude, latitude] = coordinates
      if (indexX) {
        const payload: IAdvancedAnalyticsApiPayload = {
          PointName: pointName,
          OrderId: orderId,
          OriginalOrderId: orderId,
          Longitude: longitude,
          Latitude: latitude,
          StartDate: startDate,
          EndDate: endDate,
          Title: indexX.Name,
          AcronymX: indexX.ShortName ? indexX.ShortName : indexX.Name,
          ColumnIndexX: indexX.Index,
          IntervalCountX: 16,
          AnalyticType: ADVANCED_ANALYTICS.EXTREME_VALUES,
          DatasetId: previewDatasetId,
          Thresholds: '',
          Duration: '1',
          Percentile: 50,
          Sign1: 'below',
          OriginalDatasetId: previewDatasetId,
          enableConditional: false,
          OrderId2: orderId,
          DatasetId2: previewDatasetId,
          AcronymY: indexX.ShortName ? indexX.ShortName : indexX.Name,
          TitleY: indexX.Name,
          ColumnIndexY: indexX.Index,
          Thresholds2: '',
          Sign2: 'below',
          IntervalCountY: 16,
        }
        onUpdate(payload)
      }
    },
    [coordinates, onUpdate, previewDatasetId, pointName]
  )

  const onUpdateExtremeValues = useCallback(
    (dateRange?: any) => {
      let variable = variableX
      if (!variable && variableXIndex !== undefined && variableXIndex !== null) {
        variable = variablesX[variableXIndex]
      }
      if (enrichedMetadata && variable) {
        const task = enrichedMetadata.find(
          (task: IEnrichedMetadata) =>
            task.DatasetId === dataset &&
            task.CoordinatesString === coordinates.join()
        )

        if (task) {
          getImage(
            variable,
            task.OrderId,
            dateRange ? dateRange[0].format(DT0Format) : startDate.format(DT0Format),
            dateRange ? dateRange[1].format(DT0Format) : endDate.format(DT0Format),
          )
        }
      }
    },
    [enrichedMetadata, dataset, startDate, endDate, variableX, variableXIndex, variablesX, pointName]
  )

  const doVariableChange = useCallback((active: number) => {
    selfTriggered.current = true
    const item = variablesX[active]
    setVariableX(item)
    setVariableXIndex(active)
  }, [variablesX])

  const doStartDateChange = useCallback((value: any) => {
    if (value) {
      setStartDate(value)
      if (isForAllAnalytics) {
        setGlobalDateRange([value, endDate])
      }
    }
  }, [endDate, isForAllAnalytics, setGlobalDateRange])

  const doEndDateChange = useCallback((value: any) => {
    if (value) {
      setEndDate(value)
      if (isForAllAnalytics) {
        setGlobalDateRange([startDate, value])
      }
    }
  }, [startDate, isForAllAnalytics, setGlobalDateRange])

  const onSelectDataset = useCallback((value: string) => {
    onDatasetChange(value)
  }, [onDatasetChange])

  const handleCheckboxChange = useCallback(() => {
    setGlobalDateRange([startDate, endDate])
    setIsForAllAnalytics(!isForAllAnalytics)
    selfTriggered.current = true
  }, [startDate, endDate, setGlobalDateRange, isForAllAnalytics, setIsForAllAnalytics])

  const disabledStartDate = useCallback((current: Moment) => {
    const isAfterEndDate = current && current > endDate
    return isAfterEndDate
      ? isAfterEndDate
      : current.isBefore(dateRange[0], 'day')
  }, [endDate, dateRange])

  const disabledEndDate = useCallback((current: Moment) => {
    const isBeforeStartDate = current && current < startDate
    return isBeforeStartDate
      ? isBeforeStartDate
      : current.isAfter(dateRange[1], 'day')
  }, [startDate, dateRange])

  const categories = useMemo(() => {
    const label = datasets.map((bd: IBestDataset) => bd.label)
    return Array.from(new Set(label))
  }, [datasets])

  useEffect(() => {
    updateUserInput({
      ExtremeValues: {
        dataset: dataset,
        variable: variableXIndex,
        startDate: startDate,
        endDate: endDate,
      },
    })
  }, [dataset, variableXIndex, startDate, endDate])

  useEffect(() => {
    if (enrichedMetadata) {
      const task = enrichedMetadata.find(
        (task: IEnrichedMetadata) =>
          task.DatasetId === previewDatasetId &&
          task.CoordinatesString === coordinates.join()
      )
      if (task) {
        const allowedItems = task.ExtremeValueDefaultItems.split('|')
        const itemsInfos = task.ItemsInfo.filter((itemInfo: IPlotVariable) =>
          itemInfo.ShortName
            ? allowedItems.includes(itemInfo.ShortName)
            : allowedItems.includes(itemInfo.Name)
        )
        if (itemsInfos.length > 0) {
          const wlItem = itemsInfos.find((item) => item.ShortName === 'WL')
          let indexX
          if (wlItem !== undefined) {
            // Backend requires that WL is separated into 2 items.
            const withoutWL = itemsInfos.filter(
              (item) => item.ShortName !== 'WL'
            )
            const modifieditems = withoutWL.concat([
              {
                ...wlItem,
                ShortName: 'WL-positive',
                Name: 'Water Level - High (positive)',
              },
              {
                ...wlItem,
                ShortName: 'WL-negative',
                Name: 'Water Level - High (negative)',
              },
            ])
            setVariablesX(modifieditems)
            indexX = modifieditems[0]
          } else {
            setVariablesX(itemsInfos)
            indexX = itemsInfos[0]
          }
          if (previewDatasetId && !isEmpty(datasets) && !isEmpty(coordinates) && extremeValuesImage === '') {
            setDataset(previewDatasetId)
            let dateRange = [startDate, endDate]
            setVariableX(indexX)
            if (isForAllAnalytics) {
              dateRange = globalDateRange
              setStartDate(dateRange[0])
              setEndDate(dateRange[1])
            }
            selfTriggered.current = true
            getImage(
              indexX,
              task.OrderId,
              dateRange[0].format(DT0Format),
              dateRange[1].format(DT0Format)
            )
          }
        }
      }
    }
  }, [previewDatasetId, datasets, enrichedMetadata, coordinates, extremeValuesImage])

  useEffect(() => {
    if (isForAllAnalytics && !isEmpty(variablesX) && extremeValuesImage !== '' ) {
      if (selfTriggered.current) {
        selfTriggered.current = false
        return
      }
      if (globalDateRange[0] !== startDate || globalDateRange[1] !== endDate) {
        onUpdateExtremeValues(globalDateRange)
      }
      setStartDate(globalDateRange[0])
      setEndDate(globalDateRange[1])
    }
  }, [isForAllAnalytics, extremeValuesImage, variablesX])

  return {
    variablesX,
    dataset,
    variableX,
    variableXIndex,
    startDate,
    endDate,
    categories,
    onUpdateExtremeValues,
    doVariableChange,
    doStartDateChange,
    doEndDateChange,
    onSelectDataset,
    handleCheckboxChange,
    disabledStartDate,
    disabledEndDate,
  }
}
